import React, { useEffect, useState } from 'react';

/**
 * One-shot mobile coach mark.
 *
 * Shows a small tooltip with an arrow pointing down at the bottom toolbar of
 * an editor. Persists "seen" state in localStorage under `storageKey` so it
 * never reappears for that user after the first visit. Auto-dismisses after
 * `autoDismissMs` (default 4000ms) and dismisses immediately on tap.
 *
 * Usage:
 *   <MobileCoachMark
 *     storageKey="realprop.reel.mobile-coach.v1"
 *     label="Tap an icon to open Assets, Layers, Design or Audio"
 *     bottomOffsetPx={64}
 *   />
 */
const MobileCoachMark = ({
  storageKey,
  label,
  bottomOffsetPx = 64,
  autoDismissMs = 4000,
}) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !storageKey) return;
    try {
      if (window.localStorage.getItem(storageKey)) return;
    } catch (_) {
      // Private mode / quota — fall through and just show once per session.
    }
    // Defer briefly so the toolbar has rendered before the arrow is drawn.
    const showTimer = setTimeout(() => setVisible(true), 350);
    const hideTimer = setTimeout(() => dismiss(), autoDismissMs + 350);
    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey, autoDismissMs]);

  const dismiss = () => {
    setVisible(false);
    if (storageKey) {
      try { window.localStorage.setItem(storageKey, '1'); } catch (_) { /* noop */ }
    }
  };

  if (!visible) return null;

  return (
    <div
      onClick={dismiss}
      className="fixed inset-x-0 z-[60] flex justify-center pointer-events-none"
      style={{ bottom: bottomOffsetPx + 12 }}
      data-testid="mobile-coach-mark"
    >
      <div className="pointer-events-auto relative bg-foreground text-background text-xs px-3 py-2 rounded-lg shadow-xl max-w-[280px] text-center animate-[fadeIn_180ms_ease-out]">
        {label}
        {/* Down-pointing arrow */}
        <div
          className="absolute left-1/2 -translate-x-1/2 -bottom-1 w-2.5 h-2.5 rotate-45 bg-foreground"
          aria-hidden="true"
        />
      </div>
    </div>
  );
};

export default MobileCoachMark;
